/**
 * 미리보기 지원 포맷의 단일 정의.
 *
 * 서버(generator)와 클라이언트(다운로드 페이지, OG 메타데이터)가 함께 쓴다.
 * 이 파일은 순수 상수/함수만 두어 클라이언트 번들에 안전하게 포함될 수 있게 한다
 * (sharp·child_process 등 서버 전용 모듈을 import하지 말 것).
 *
 * 새 포맷을 추가할 때 손대는 곳:
 *   1. 아래 확장자/MIME 목록
 *   2. 해당 PreviewGenerator 구현 (없으면 신규 작성 후 preview-generators/index.ts에 등록)
 *   3. lib/file-handler.ts의 MIME 매핑 (업로드 시 mime_type이 채워지도록)
 */

/** 확장자 목록에 해당하는지. 파일명이 비어 있으면 false. */
function hasExt(fileName: string | undefined, exts: readonly string[]): boolean {
  if (!fileName) return false;
  const lower = fileName.toLowerCase();
  return exts.some(e => lower.endsWith(e));
}

function matches(
  mimeType: string | undefined,
  fileName: string | undefined,
  mimes: readonly string[],
  exts: readonly string[],
): boolean {
  return (!!mimeType && mimes.includes(mimeType)) || hasExt(fileName, exts);
}

// ---------------------------------------------------------------------------
// 이미지
// ---------------------------------------------------------------------------

/**
 * 브라우저/크롤러가 직접 렌더링하지 못해 항상 변환이 필요한 이미지 포맷.
 * TIFF는 Safari 외 브라우저가, PSD는 모든 브라우저가 표시하지 못한다.
 */
export const CONVERTIBLE_IMAGE_MIMES = [
  'image/bmp',
  'image/heic',
  'image/heif',
  'image/avif',
  'image/tiff',
  'image/vnd.adobe.photoshop',
] as const;

export const CONVERTIBLE_IMAGE_EXTS = [
  '.bmp', '.heic', '.heif', '.avif', '.tif', '.tiff', '.psd',
] as const;

/** 브라우저가 직접 렌더링 가능한 래스터 이미지 (variant 요청 시에만 썸네일 생성) */
export const RASTER_IMAGE_MIMES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/jp2',
] as const;

export const RASTER_IMAGE_EXTS = [
  '.jpg', '.jpeg', '.png', '.webp', '.gif', '.jp2',
] as const;

/** sharp(libvips)가 디코드하지 못해 ffmpeg 폴백이 필요한 포맷 */
export const SHARP_UNSUPPORTED_MIMES = ['image/bmp', 'image/vnd.adobe.photoshop'] as const;
export const SHARP_UNSUPPORTED_EXTS = ['.bmp', '.psd'] as const;

// ---------------------------------------------------------------------------
// 카메라 RAW
// ---------------------------------------------------------------------------

/**
 * 카메라 RAW(디지털 네거티브) 포맷.
 *
 * sharp/libvips(Alpine 빌드)도 ffmpeg도 디코드하지 못하므로 exiftool + LibRaw를
 * 쓰는 전용 generator(preview-generators/raw.ts)가 처리한다.
 * MIME은 표준이 없어 관행적인 `image/x-<vendor>-<ext>`를 쓴다 — 확장자 매칭이 사실상의 기준.
 */
export const CAMERA_RAW_MIMES = [
  'image/x-adobe-dng',
  'image/x-sony-arw', 'image/x-sony-srf', 'image/x-sony-sr2',
  'image/x-canon-cr2', 'image/x-canon-cr3', 'image/x-canon-crw',
  'image/x-nikon-nef', 'image/x-nikon-nrw',
  'image/x-fuji-raf',
  'image/x-olympus-orf',
  'image/x-panasonic-rw2',
  'image/x-pentax-pef',
  'image/x-samsung-srw',
  'image/x-minolta-mrw',
  'image/x-sigma-x3f',
  'image/x-epson-erf',
  'image/x-kodak-dcr', 'image/x-kodak-kdc',
  'image/x-hasselblad-3fr', 'image/x-hasselblad-fff',
  'image/x-phaseone-iiq',
  'image/x-leica-rwl',
] as const;

export const CAMERA_RAW_EXTS = [
  '.dng',
  '.arw', '.srf', '.sr2',
  '.cr2', '.cr3', '.crw',
  '.nef', '.nrw',
  '.raf',
  '.orf',
  '.rw2',
  '.pef',
  '.srw',
  '.mrw',
  '.x3f',
  '.erf',
  '.dcr', '.kdc',
  '.3fr', '.fff',
  '.iiq',
  '.rwl',
] as const;

export function isCameraRaw(mimeType?: string, fileName?: string): boolean {
  return matches(mimeType, fileName, CAMERA_RAW_MIMES, CAMERA_RAW_EXTS);
}

/** sharp/ffmpeg 경로로 변환하는 이미지. RAW는 별도 generator라 제외된다. */
export function isConvertibleImage(mimeType?: string, fileName?: string): boolean {
  return matches(mimeType, fileName, CONVERTIBLE_IMAGE_MIMES, CONVERTIBLE_IMAGE_EXTS);
}

/** 항상 /api/preview/로 변환해야 하는 이미지인지 */
export function needsImageConversion(mimeType?: string, fileName?: string): boolean {
  return isConvertibleImage(mimeType, fileName) || isCameraRaw(mimeType, fileName);
}

export function isRasterImage(mimeType?: string, fileName?: string): boolean {
  return matches(mimeType, fileName, RASTER_IMAGE_MIMES, RASTER_IMAGE_EXTS);
}

export function needsFfmpegFallback(mimeType?: string, fileName?: string): boolean {
  return matches(mimeType, fileName, SHARP_UNSUPPORTED_MIMES, SHARP_UNSUPPORTED_EXTS);
}

// ---------------------------------------------------------------------------
// 문서 (Ghostscript로 첫 페이지 렌더)
// ---------------------------------------------------------------------------

/** .ai는 현대 Illustrator가 PDF 호환 래퍼로 저장하므로 같은 경로를 탄다. */
export const DOCUMENT_RENDER_MIMES = ['application/pdf', 'application/postscript'] as const;
export const DOCUMENT_RENDER_EXTS = ['.pdf', '.ai'] as const;

export function isRenderableDocument(mimeType?: string, fileName?: string): boolean {
  return matches(mimeType, fileName, DOCUMENT_RENDER_MIMES, DOCUMENT_RENDER_EXTS);
}

// ---------------------------------------------------------------------------
// 실행 파일 / 패키지
// ---------------------------------------------------------------------------

export const EXECUTABLE_MIMES = ['application/x-msdownload', 'application/x-dosexec'] as const;
export const EXECUTABLE_EXTS = ['.exe', '.dll'] as const;

export function isExecutable(mimeType?: string, fileName?: string): boolean {
  return matches(mimeType, fileName, EXECUTABLE_MIMES, EXECUTABLE_EXTS);
}

export const UNITY_PACKAGE_EXTS = ['.unitypackage'] as const;

export function isUnityPackage(fileName?: string): boolean {
  return hasExt(fileName, UNITY_PACKAGE_EXTS);
}

/**
 * CLIP STUDIO PAINT 파일.
 * GTA V 리플레이도 .clip을 쓰므로 generator가 CSFCHUNK 매직을 다시 확인한다.
 */
export const CLIP_STUDIO_EXTS = ['.clip'] as const;

export function isClipStudioFile(mimeType?: string, fileName?: string): boolean {
  return mimeType === 'application/x-clip-studio' || hasExt(fileName, CLIP_STUDIO_EXTS);
}

// ---------------------------------------------------------------------------
// 종합 판별
// ---------------------------------------------------------------------------

/**
 * /api/preview/가 썸네일을 만들어낼 수 있는 파일인지.
 * 오디오는 내장 앨범아트가 있을 때만 생성되므로 여기서 제외한다
 * (호출부는 media-info의 hasCover로 판단할 것).
 */
export function hasPreviewThumbnail(mimeType?: string, fileName?: string): boolean {
  if (mimeType?.startsWith('video/')) return true;
  if (isUnityPackage(fileName)) return true;
  if (isExecutable(mimeType, fileName)) return true;
  if (needsImageConversion(mimeType, fileName)) return true;
  if (isRenderableDocument(mimeType, fileName)) return true;
  if (isClipStudioFile(mimeType, fileName)) return true;
  return false;
}

/** img 태그로 원본/렌더 결과를 그대로 띄울 수 있는 파일 */
export function isViewableAsImage(mimeType?: string, fileName?: string): boolean {
  return mimeType?.startsWith('image/') === true ||
         isRenderableDocument(mimeType, fileName) ||
         isClipStudioFile(mimeType, fileName) ||
         isCameraRaw(mimeType, fileName);
}
